// Groupe de comptes à l'import Tradovate, choisi COMPTE PAR COMPTE.
//
// PROBLÈME
// L'écran d'import n'offrait qu'un interrupteur « Regrouper ces comptes » et un
// nom, appliqués à tous les comptes neufs, et seulement à partir de deux. Un
// utilisateur qui ajoutait UN compte chez une prop firm où il en suivait déjà
// d'autres le voyait arriver hors groupe, et devait aller l'y ranger à la main.
//
// DÉSORMAIS
// - le serveur suggère le groupe qui contient déjà les comptes de cette prop
//   firm (`suggestImportGroupId`) ;
// - chaque ligne porte son propre choix (aucun, groupe existant, nouveau
//   groupe), pré-rempli avec cette suggestion ;
// - un client qui n'envoie aucun choix par ligne (ancien bundle) garde
//   l'ancien comportement (`resolveImportGroupTargets`, mode historique).
//
// Module PUR : aucun import Wasp/Prisma, testable isolément.

/** Valeur du sélecteur pour « nouveau groupe » (nom saisi une seule fois). */
export const NEW_GROUP_VALUE = "__new__";
/** Valeur du sélecteur pour « aucun groupe ». */
export const NO_GROUP_VALUE = "none";

export interface GroupSuggestionAccount {
  groupId: string | null;
  propFirmTemplateId: string | null;
  isActive?: boolean | null;
}

export interface GroupSuggestionGroup {
  id: string;
  propFirmTemplateId: string | null;
  createdAt?: Date;
}

/**
 * Groupe dans lequel ranger par défaut un compte neuf de `propFirmTemplateId`.
 *
 * 1. Le groupe qui contient le plus de comptes ACTIFS de cette prop firm (le
 *    template du compte, ou à défaut celui de son groupe) ;
 * 2. à défaut, même calcul sur les comptes inactifs (prop firm dont tous les
 *    comptes ont été cramés ou clôturés : le groupe reste le bon) ;
 * 3. à défaut, un groupe rattaché lui-même à cette prop firm.
 *
 * Égalité : le plus ancien, pour une suggestion déterministe. Sans prop firm,
 * aucune suggestion : regrouper « tous les comptes sans prop firm » n'a pas de
 * sens.
 */
export function suggestImportGroupId(
  propFirmTemplateId: string | null,
  accounts: readonly GroupSuggestionAccount[],
  groups: readonly GroupSuggestionGroup[],
): string | null {
  if (!propFirmTemplateId) return null;
  const groupById = new Map(groups.map((g) => [g.id, g]));

  const pick = (candidates: readonly GroupSuggestionAccount[]): string | null => {
    const counts = new Map<string, number>();
    for (const a of candidates) {
      if (!a.groupId) continue;
      const group = groupById.get(a.groupId);
      if (!group) continue;
      const template = a.propFirmTemplateId ?? group.propFirmTemplateId;
      if (template !== propFirmTemplateId) continue;
      counts.set(a.groupId, (counts.get(a.groupId) ?? 0) + 1);
    }
    let best: string | null = null;
    for (const [id, count] of counts) {
      if (best === null) {
        best = id;
        continue;
      }
      const bestCount = counts.get(best)!;
      if (count > bestCount || (count === bestCount && isOlder(groupById.get(id)!, groupById.get(best)!))) {
        best = id;
      }
    }
    return best;
  };

  const fromActive = pick(accounts.filter((a) => a.isActive !== false));
  if (fromActive) return fromActive;
  const fromAll = pick(accounts);
  if (fromAll) return fromAll;

  const byTemplate = groups
    .filter((g) => g.propFirmTemplateId === propFirmTemplateId)
    .reduce<GroupSuggestionGroup | null>((oldest, g) => (!oldest || isOlder(g, oldest) ? g : oldest), null);
  return byTemplate?.id ?? null;
}

function isOlder(a: GroupSuggestionGroup, b: GroupSuggestionGroup): boolean {
  return (a.createdAt?.getTime() ?? Infinity) < (b.createdAt?.getTime() ?? Infinity);
}

/**
 * Valeur initiale du sélecteur de groupe d'une ligne NEUVE : le groupe
 * suggéré, sinon un nouveau groupe quand plusieurs comptes neufs arrivent
 * ensemble (l'ancien défaut de l'interrupteur), sinon aucun.
 */
export function initialRowGroupValue(suggestedGroupId: string | null, newRowCount: number): string {
  if (suggestedGroupId) return suggestedGroupId;
  return newRowCount >= 2 ? NEW_GROUP_VALUE : NO_GROUP_VALUE;
}

export type ImportGroupTarget = { kind: "existing"; groupId: string } | { kind: "new" } | null;

export interface ImportGroupRow {
  /** Groupe existant choisi pour la ligne (null = aucun). Absent = ancien client. */
  groupId?: string | null;
  /** true = la ligne rejoint le groupe NOUVEAU nommé `groupName`. */
  newGroup?: boolean;
}

/**
 * Cible de groupe de chaque compte à créer, dans l'ordre de `rows`.
 *
 * Mode historique (aucune ligne ne porte de choix) : le groupe nommé
 * `groupName` pour tous, à partir de deux comptes, exactement comme avant.
 *
 * Un « nouveau groupe » sans nom exploitable retombe sur « aucun groupe »
 * plutôt que de créer un groupe au nom vide.
 */
export function resolveImportGroupTargets(
  rows: readonly ImportGroupRow[],
  groupName: string | undefined,
): ImportGroupTarget[] {
  const name = groupName?.trim() ?? "";
  const perRow = rows.some((r) => r.groupId !== undefined || r.newGroup !== undefined);
  if (!perRow) {
    const legacy: ImportGroupTarget = rows.length >= 2 && name ? { kind: "new" } : null;
    return rows.map(() => legacy);
  }
  return rows.map((r) => {
    if (r.newGroup) return name ? { kind: "new" } : null;
    return r.groupId ? { kind: "existing", groupId: r.groupId } : null;
  });
}
